// export.ts — CSV export of the practice history (built on-device from local data).
// One row per question across every session, so the whole history rereads in a spreadsheet.

import type { Session, SessionQuestion } from "./store";
import { SENIORITY_LABELS, TYPE_LABELS, MODE_LABELS, parseFeedback, themeLabel } from "./store";

function cell(v: string | number): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csv(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows].map((r) => r.map(cell).join(",")).join("\n");
}

/** Full CSV: Session date, Role, Company, Seniority, Type, Mode, #, Question, Answer,
 *  Answer seconds, Strengths, Gaps, STAR note, Better example, Themes. */
export function practiceCsv(sessions: Session[], questionsBySession: Map<string, SessionQuestion[]>): string {
  const rows: (string | number)[][] = [];
  for (const s of sessions) {
    const qs = questionsBySession.get(s.id) ?? [];
    for (const q of qs) {
      const fb = parseFeedback(q.feedback_json);
      rows.push([
        s.created_at.slice(0, 10),
        s.role,
        s.company,
        SENIORITY_LABELS[s.seniority] ?? s.seniority,
        TYPE_LABELS[s.interview_type] ?? s.interview_type,
        MODE_LABELS[s.mode] ?? s.mode,
        q.idx + 1,
        q.question,
        q.answer,
        q.answer_seconds > 0 ? q.answer_seconds : "",
        fb ? fb.strengths.join("; ") : "",
        fb ? fb.gaps.join("; ") : "",
        fb ? fb.star_note : "",
        fb ? fb.better_example : "",
        fb ? fb.themes.map(themeLabel).join("; ") : "",
      ]);
    }
  }
  return csv(
    ["Session date", "Role", "Company", "Seniority", "Type", "Mode", "#", "Question", "Answer", "Answer seconds", "Strengths", "Gaps", "STAR note", "Better example", "Themes"],
    rows,
  );
}

export function buildExport(sessions: Session[], questionsBySession: Map<string, SessionQuestion[]>): { csv: string; filename: string } {
  const stamp = new Date().toISOString().slice(0, 10);
  return { csv: practiceCsv(sessions, questionsBySession), filename: `interview-${stamp}.csv` };
}
